import { queryClient } from './queryClient';

// Track API request cache if you're using any
interface CacheEntry {
  timestamp: number;
  data: any;
}

// Simple in-memory cache with TTL
class APICache {
  private static instance: APICache;
  private cache: Map<string, CacheEntry> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): APICache {
    if (!APICache.instance) {
      APICache.instance = new APICache();
    }
    return APICache.instance;
  }

  set(key: string, data: any, ttl: number = this.DEFAULT_TTL) {
    this.cache.set(key, {
      timestamp: Date.now(),
      data
    });
    
    // Auto-expire after TTL
    setTimeout(() => {
      this.cache.delete(key);
    }, ttl);
  }

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() - entry.timestamp > this.DEFAULT_TTL) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  clear() {
    this.cache.clear();
    console.log('[APICache] Cache cleared');
  }

  // Clear cache for specific user/workspace
  clearByPattern(pattern: RegExp) {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (pattern.test(key)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

export const apiCache = APICache.getInstance();

// Comprehensive cache clearing function
export const clearAllCaches = () => {
  // Clear API cache
  apiCache.clear();

  // Clear React Query cache. This is the primary data cache for the dashboard
  // and most data hooks — without this, the next user sees the previous user's
  // cached results on login until each query refetches in the background.
  queryClient.cancelQueries();
  queryClient.clear();

  // Clear SWR cache if you're using it
  if (window.swrCache) {
    window.swrCache.clear();
  }

  // Clear any service worker caches
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach(name => {
        caches.delete(name);
      });
    });
  }

  // Clear all localStorage except whitelisted items. Prefix whitelist covers
  // keys that must survive both logout and login (e.g. the user-scoped
  // "skip for this browser" branding reminder dismissal).
  const whitelist = ['user-storage', 'theme', 'language'];
  const prefixWhitelist = ['persist:', 'branding-reminder-snoozed-browser:'];
  Object.keys(localStorage).forEach(key => {
    if (whitelist.includes(key)) return;
    if (prefixWhitelist.some(prefix => key.startsWith(prefix))) return;
    localStorage.removeItem(key);
  });

  // Clear sessionStorage
  sessionStorage.clear();

  console.log('[CacheCleanup] All caches cleared');
};

// Add type declaration for window
declare global {
  interface Window {
    swrCache?: { clear: () => void };
  }
}